import { useMemo, useRef, useState } from "react";
import { BadgeCheck, ClipboardList, Eye, ListX, RotateCcw, Search } from "lucide-react";
import CrmColumnHeading from "@/components/CrmColumnHeading";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { WAITLIST_REGISTRANT_COPY } from "@shared/registrationWaitlist";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { PendingQueueEventSummary, PendingQueueStatus, PendingRegistration, PendingRegistrationStatus } from "@shared/registrationApproval";
import {
  classifyRegistrationError,
  describeRegistrationError,
  shouldRefreshAfter,
  useEventsApprove,
  useEventsDecline,
  useEventsListPending,
  useEventsReopen,
  useRefreshEventsListPending,
  type RegistrationErrorKind,
} from "@/lib/registrationApproval";

const surface: React.CSSProperties = {
  background: "rgba(13,18,32,0.82)",
  border: "1px solid rgba(255,255,255,0.075)",
  borderRadius: "0.95rem",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 40,
  boxSizing: "border-box",
  border: "1px solid rgba(255,255,255,0.13)",
  borderRadius: "0.58rem",
  background: "rgba(255,255,255,0.045)",
  color: "#F8FAFC",
  padding: "0.6rem 0.7rem",
  outline: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "var(--chi-body-compact)",
};

const STATUS_META: Record<PendingRegistrationStatus, { label: string; color: string; background: string; border: string }> = {
  pending: { label: "Pending review", color: "#FCD34D", background: "rgba(252,211,77,.10)", border: "rgba(252,211,77,.28)" },
  waitlisted: { label: "Waitlisted", color: "#FDBA74", background: "rgba(251,146,60,.10)", border: "rgba(251,146,60,.28)" },
  registered: { label: "Registered", color: "#86EFAC", background: "rgba(74,222,128,.10)", border: "rgba(74,222,128,.28)" },
};

const QUEUE_TABS: ReadonlyArray<{ value: PendingQueueStatus; label: string }> = [
  { value: "pending", label: "Pending" },
  { value: "waitlisted", label: "Waitlisted" },
];

export function formatSeatsRemaining(seatsRemaining: number | null) {
  if (seatsRemaining === null) return "No seat limit";
  return `${seatsRemaining} ${seatsRemaining === 1 ? "seat" : "seats"} left`;
}

function describeEventCounts(event: PendingQueueEventSummary) {
  return `${event.pendingCount} pending · ${formatSeatsRemaining(event.seatsRemaining)}`;
}

function formatTimestamp(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function StatusPill({ status }: { status: PendingRegistrationStatus }) {
  const meta = STATUS_META[status];
  return (
    <span style={{ display: "inline-flex", padding: ".28rem .46rem", borderRadius: "999px", color: meta.color, background: meta.background, border: `1px solid ${meta.border}`, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, whiteSpace: "nowrap" }}>
      {meta.label}
    </span>
  );
}

type ActionKind = "approve" | "reopen" | "decline";
type ActionError = { message: string; kind: RegistrationErrorKind };

/**
 * Per-registration in-flight and error state shared by the table rows and the
 * detail sheet. A ref guards against a second click landing before React has
 * re-rendered the disabled button; status itself only ever comes back from the
 * backend procedures.
 */
function useRegistrationActions() {
  const approve = useEventsApprove();
  const reopen = useEventsReopen();
  const decline = useEventsDecline();
  const refresh = useRefreshEventsListPending();
  const inFlight = useRef(new Set<number>());
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set());
  const [errors, setErrors] = useState<Record<number, ActionError>>({});

  async function run(registration: PendingRegistration, kind: ActionKind) {
    const { id, reviewVersion } = registration;
    if (inFlight.current.has(id)) return;
    inFlight.current.add(id);
    setBusyIds(new Set(inFlight.current));
    const mutation = kind === "approve" ? approve : kind === "reopen" ? reopen : decline;
    try {
      const result = await mutation.mutateAsync({ registrationId: id, expectedVersion: reviewVersion });
      if (!result.ok) throw new Error("The registration could not be updated. Please try again.");
      setErrors(current => {
        const next = { ...current };
        delete next[id];
        return next;
      });
    } catch (error) {
      const errorKind = classifyRegistrationError(error);
      setErrors(current => ({ ...current, [id]: { message: describeRegistrationError(error), kind: errorKind } }));
      if (shouldRefreshAfter(errorKind)) await refresh();
    } finally {
      inFlight.current.delete(id);
      setBusyIds(new Set(inFlight.current));
    }
  }

  return { busyIds, errors, run };
}

type RegistrationActions = ReturnType<typeof useRegistrationActions>;

function ActionErrorNotice({ registration, actions }: { registration: PendingRegistration; actions: RegistrationActions }) {
  const error = actions.errors[registration.id];
  if (!error) return null;
  if (shouldRefreshAfter(error.kind)) {
    return (
      <p role="status" style={{ margin: ".45rem 0 0", color: "#BAE6FD", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>
        {error.message}
      </p>
    );
  }
  return (
    <div role="alert" style={{ marginTop: ".45rem", padding: ".5rem .6rem", borderRadius: ".5rem", border: "1px solid rgba(251,113,133,.35)", background: "rgba(251,113,133,.08)", color: "#FECDD3", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>
      <p style={{ margin: 0 }}>{error.message}</p>
      {error.kind === "event_full" && registration.actions.includes("decline") && (
        <button
          type="button"
          className="speaker-crm-review__secondary"
          style={{ marginTop: ".4rem" }}
          disabled={actions.busyIds.has(registration.id)}
          onClick={() => void actions.run(registration, "decline")}
        >
          Waitlist instead
        </button>
      )}
    </div>
  );
}

/**
 * Decline for a pending row: moves the registration to the waitlist through the
 * same `events.decline` procedure the roster button uses. The server decides
 * whether the change is allowed; this only asks, after the admin confirms, because
 * the registrant is told they are on the waitlist.
 */
function QueueDeclineButton({ registration, actions }: { registration: PendingRegistration; actions: RegistrationActions }) {
  const [confirming, setConfirming] = useState(false);
  if (!registration.actions.includes("decline")) return null;
  const busy = actions.busyIds.has(registration.id);
  return (
    <>
      <button
        type="button"
        className="speaker-crm-review__secondary"
        disabled={busy}
        aria-label={`Decline ${registration.name} and add to the waitlist`}
        onClick={() => setConfirming(true)}
      >
        <ListX size={13} aria-hidden="true" style={{ marginRight: ".3rem" }} /> {busy ? "Declining…" : "Decline"}
      </button>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline {registration.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They move to the waitlist and are told: “{WAITLIST_REGISTRANT_COPY}”
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void actions.run(registration, "decline")}>Decline</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RegistrationDetailSheet({ registration, inList, actions, onClose }: { registration: PendingRegistration | null; inList: boolean; actions: RegistrationActions; onClose: () => void }) {
  const busy = registration ? actions.busyIds.has(registration.id) : false;
  const rows: Array<[string, React.ReactNode]> = registration
    ? [
        ["Email", registration.email],
        ["Member status", registration.isMember ? "Member" : "Not a member"],
        ["Event", registration.eventName],
        ["Submitted", formatTimestamp(registration.submittedAt)],
        ["Status", <StatusPill key="status" status={registration.status} />],
        ...(registration.stateChangedAt ? [["Status changed", formatTimestamp(registration.stateChangedAt)] as [string, React.ReactNode]] : []),
        ...(registration.stateChangedByOpenId ? [["Changed by", registration.stateChangedByOpenId] as [string, React.ReactNode]] : []),
        ...(registration.stateChangeReason ? [["Reason", registration.stateChangeReason] as [string, React.ReactNode]] : []),
      ]
    : [];
  return (
    <Sheet open={registration !== null} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent style={{ background: "#0D1220", color: "#E2E8F0" }}>
        {registration && (
          <>
            <SheetHeader>
              <SheetTitle style={{ color: "#FFF" }}>{registration.name}</SheetTitle>
              <SheetDescription>Registration for {registration.eventName}</SheetDescription>
            </SheetHeader>
            <div style={{ padding: "0 1rem", display: "grid", gap: ".75rem" }}>
              <dl style={{ margin: 0, display: "grid", gap: ".6rem" }}>
                {rows.map(([label, value]) => (
                  <div key={label}>
                    <dt className="speaker-crm-detail__label">{label}</dt>
                    <dd className="speaker-crm-detail__value" style={{ margin: 0 }}>{value}</dd>
                  </div>
                ))}
              </dl>
              {!inList && (
                <p role="status" className="speaker-crm-detail__value">This registration was updated and is no longer in this list.</p>
              )}
              {inList && <div style={{ display: "flex", gap: ".45rem", flexWrap: "wrap" }}>
                {registration.actions.includes("approve") && (
                  <button type="button" className="speaker-crm-detail__cta" style={{ margin: 0 }} disabled={busy} onClick={() => void actions.run(registration, "approve")}>
                    <BadgeCheck size={13} aria-hidden="true" /> {busy ? "Approving…" : "Approve"}
                  </button>
                )}
                <QueueDeclineButton registration={registration} actions={actions} />
                {registration.actions.includes("reopen") && (
                  <button type="button" className="speaker-crm-detail__cta" style={{ margin: 0 }} disabled={busy} onClick={() => void actions.run(registration, "reopen")}>
                    <RotateCcw size={13} aria-hidden="true" /> {busy ? "Re-opening…" : "Re-open"}
                  </button>
                )}
              </div>}
              <ActionErrorNotice registration={registration} actions={actions} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function RegistrationApprovalQueueView() {
  const [statusFilter, setStatusFilter] = useState<PendingQueueStatus>("pending");
  const [eventFilter, setEventFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState<number | null>(null);
  const lastDetail = useRef<PendingRegistration | null>(null);
  const query = useEventsListPending({ status: statusFilter, eventId: eventFilter === "all" ? undefined : eventFilter, search });
  const actions = useRegistrationActions();

  const pages = query.data?.pages ?? [];
  const latestPage = pages.at(-1);
  const events = latestPage?.events ?? [];
  const total = latestPage?.total ?? 0;
  const items = useMemo(() => pages.flatMap(page => page.items), [pages]);

  const detailInList = detailId !== null && items.some(item => item.id === detailId);
  const detail = detailId === null ? null : items.find(item => item.id === detailId) ?? (lastDetail.current?.id === detailId ? lastDetail.current : null);
  lastDetail.current = detail;

  if (query.isLoading) {
    return <div style={{ flex: 1, display: "grid", placeItems: "center", color: "#94A3B8" }}>Loading registrations awaiting approval…</div>;
  }

  if (query.isError) {
    return (
      <div style={{ flex: 1, display: "grid", placeItems: "center", padding: "2rem" }}>
        <div style={{ ...surface, maxWidth: 540, padding: "2rem", textAlign: "center" }}>
          <h2 style={{ margin: 0, color: "#FFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "2rem" }}>The approval queue is unavailable</h2>
          <p style={{ margin: ".75rem 0 1.25rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", lineHeight: 1.6 }}>No changes were made. Try loading the queue again.</p>
          <button type="button" className="speaker-crm-review__secondary" onClick={() => query.refetch()}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "clamp(1.25rem, 3vw, 2.5rem)", width: "100%", boxSizing: "border-box" }}>
      <header style={{ marginBottom: "1.2rem" }}>
        <p style={{ margin: "0 0 .35rem", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".12em", textTransform: "uppercase" }}>Special events · Approval</p>
        <h1 style={{ margin: 0, color: "#FFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 4vw, 3.1rem)", letterSpacing: ".025em", lineHeight: 1 }}>Registration approvals</h1>
        <p style={{ margin: ".65rem 0 0", color: "#8E9AAF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6, maxWidth: 760 }}>Pending registrations do not hold a seat. A seat is taken only when a registration is approved or re-opened.</p>
      </header>

      {events.length > 0 && (
        <section aria-label="Event capacity" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: ".7rem", marginBottom: "1rem" }}>
          {events.map(event => (
            <div key={event.eventId} data-event-summary={event.eventId} style={{ ...surface, padding: ".8rem 1rem" }}>
              <p style={{ margin: 0, color: "#F4F7FA", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{event.eventName}</p>
              <p style={{ margin: ".3rem 0 0", color: "#C6B08A", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                {describeEventCounts(event)}
              </p>
            </div>
          ))}
        </section>
      )}

      <div role="tablist" aria-label="Queue" style={{ display: "inline-flex", gap: ".35rem", marginBottom: ".75rem", padding: ".25rem", borderRadius: ".7rem", border: "1px solid rgba(255,255,255,.09)", background: "rgba(255,255,255,.03)" }}>
        {QUEUE_TABS.map(tab => {
          const selected = statusFilter === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setStatusFilter(tab.value)}
              style={{ minHeight: 36, padding: ".4rem .85rem", borderRadius: ".5rem", border: `1px solid ${selected ? "rgba(0,212,255,.44)" : "transparent"}`, background: selected ? "rgba(0,212,255,.11)" : "transparent", color: selected ? "#7DEAFF" : "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: "pointer" }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <section style={{ ...surface, padding: ".72rem", marginBottom: "1rem", display: "grid", gridTemplateColumns: "minmax(240px,1fr) minmax(220px,.6fr)", gap: ".55rem" }} aria-label="Filter registrations">
        <label style={{ position: "relative" }}>
          <Search size={14} style={{ position: "absolute", left: ".7rem", top: ".8rem", color: "#738198" }} />
          <input value={search} onChange={event => setSearch(event.target.value)} style={{ ...inputStyle, paddingLeft: "2rem" }} placeholder="Search by name or email…" aria-label="Search by name or email" />
        </label>
        <select aria-label="Filter by event" value={eventFilter} onChange={event => setEventFilter(event.target.value)} style={inputStyle}>
          <option value="all">All events</option>
          {events.map(event => (
            <option key={event.eventId} value={event.eventId}>
              {event.eventName} · {describeEventCounts(event)}
            </option>
          ))}
        </select>
      </section>

      <section aria-labelledby="registration-review-queue" style={{ ...surface, borderColor: "rgba(245,166,35,.34)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", padding: "1rem 1.1rem", borderBottom: "1px solid rgba(245,166,35,.22)", background: "rgba(245,166,35,.07)" }}>
          <div>
            <h2 id="registration-review-queue" style={{ margin: 0, color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1rem", fontWeight: 700 }}>
              {total} {statusFilter === "pending" ? "pending" : "declined"} registration{total === 1 ? "" : "s"}
            </h2>
            <p style={{ margin: ".2rem 0 0", color: "#C6B08A", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Oldest first · Showing {items.length} of {total}</p>
          </div>
          <ClipboardList size={20} color="#F5A623" aria-hidden="true" />
        </div>

        {items.length === 0 ? (
          <p style={{ margin: 0, padding: "1.5rem 1.1rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            {search.trim() || eventFilter !== "all"
              ? "No registrations match these filters."
              : statusFilter === "pending" ? "No registrations are waiting for approval." : "No declined registrations can be re-opened."}
          </p>
        ) : (
          <div className="speaker-table-wrap">
            <table className="speaker-table" style={{ minWidth: 860 }}>
              <caption className="sr-only">Special-event registrations awaiting an approval decision.</caption>
              <thead>
                <tr>
                  <th scope="col"><CrmColumnHeading label="Name" /></th>
                  <th scope="col"><CrmColumnHeading label="Email" /></th>
                  <th scope="col"><CrmColumnHeading label="Event" /></th>
                  <th scope="col"><CrmColumnHeading label="Submitted" /></th>
                  <th scope="col"><CrmColumnHeading label="Actions" /></th>
                </tr>
              </thead>
              <tbody>
                {items.map(registration => {
                  const busy = actions.busyIds.has(registration.id);
                  return (
                    <tr key={registration.id} data-registration-row={registration.id}>
                      <td>
                        <strong style={{ display: "block", color: "#F4F7FA", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{registration.name}</strong>
                        <div style={{ marginTop: ".3rem" }}><StatusPill status={registration.status} /></div>
                      </td>
                      <td>
                        <span style={{ display: "block", color: "#CFD8E6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{registration.email}</span>
                        <span style={{ color: "#7E8CA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{registration.isMember ? "Member" : "Not a member"}</span>
                      </td>
                      <td style={{ color: "#CFD8E6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{registration.eventName}</td>
                      <td style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", whiteSpace: "nowrap" }}>
                        <time dateTime={registration.submittedAt}>{formatTimestamp(registration.submittedAt)}</time>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: ".45rem", flexWrap: "wrap" }}>
                          {registration.actions.includes("approve") && (
                            <button
                              type="button"
                              className="speaker-crm-detail__cta"
                              style={{ margin: 0 }}
                              disabled={busy}
                              aria-label={`Approve ${registration.name}`}
                              onClick={() => void actions.run(registration, "approve")}
                            >
                              <BadgeCheck size={13} aria-hidden="true" /> {busy ? "Approving…" : "Approve"}
                            </button>
                          )}
                          <QueueDeclineButton registration={registration} actions={actions} />
                          <button
                            type="button"
                            className="speaker-crm-review__secondary"
                            aria-label={`View details for ${registration.name}`}
                            onClick={() => setDetailId(registration.id)}
                          >
                            <Eye size={13} aria-hidden="true" style={{ marginRight: ".3rem" }} /> Details
                          </button>
                        </div>
                        <ActionErrorNotice registration={registration} actions={actions} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {query.hasNextPage && (
          <div style={{ display: "flex", justifyContent: "center", padding: ".75rem 1.1rem", borderTop: "1px solid rgba(255,255,255,.07)" }}>
            <button type="button" className="speaker-crm-review__secondary" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
              {query.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </section>

      <RegistrationDetailSheet registration={detail} inList={detailInList} actions={actions} onClose={() => setDetailId(null)} />
    </div>
  );
}
